import { useLocation } from 'react-router-dom';
import { useFeedbackMarkup } from './FeedbackMarkupProvider.jsx';
import { countFeedbackMarks, countFeedbackScreens } from './feedbackSessionCounts.js';
import { reviewToolsEnabled } from './reviewToolsCapability.js';

// Persistent session chrome shown on every route while an unsubmitted feedback
// package is active. It remains capture-safe through the existing
// feedback-capture-hiding-chrome selector.
export function FeedbackSessionBar() {
  const { state, captureScreen, completeStage, navigate, openEndSession, endFeedbackSession } =
    useFeedbackMarkup();
  const location = useLocation();

  const pkg = state.package;
  const submitted = pkg?.status === 'submitted' || Boolean(pkg?.submittedAt);
  const onFeedbackSurface = location.pathname.startsWith('/feedback');
  const selecting = state.regionSelecting;
  // TFB-02 (Benji, live 2026-07-23): while a screen is open in the editor the
  // bar owns BOTH capture intents — re-grab the screen being worked on, or
  // finish it and return to the app for a different one (the action that used
  // to sit ambiguously in the editor's top menu).
  const editingStage = Boolean(state.stage);
  const stageSource = state.stage?.metadata?.route
    ? `${state.stage.metadata.route}${state.stage.metadata.query || ''}${state.stage.metadata.hash || ''}`
    : '/home';
  // Yield to feedback overlays so the bar never intercepts their controls.
  const overlayShowing = state.overlayOpen || state.reviewOpen || state.endSessionOpen;

  if (!reviewToolsEnabled() || !pkg || submitted || selecting || overlayShowing) {
    return null;
  }

  const marks = countFeedbackMarks(state);
  const screenCount = countFeedbackScreens(state);
  const summary = [
    `${screenCount} screen${screenCount === 1 ? '' : 's'}`,
    marks ? `${marks} mark${marks === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      className={`feedback-session-bar${onFeedbackSurface ? ' on-feedback-surface' : ''}`}
      data-testid="feedback-session-bar"
      role="region"
      aria-label="Feedback active"
    >
      <span className="feedback-session-bar-pulse" aria-hidden />
      <span className="feedback-session-bar-summary">
        <strong>Feedback active</strong> · {summary}
        {screenCount === 0 ? ' — capture any screen to start' : ''}
      </span>
      <button
        type="button"
        className="btn primary sm"
        data-testid="feedback-session-bar-capture"
        onClick={captureScreen}
        disabled={state.captureStatus === 'capturing'}
      >
        {editingStage ? 'Capture this screen' : 'Capture'}
      </button>
      {editingStage ? (
        <button
          type="button"
          className="btn ghost sm"
          data-testid="feedback-capture-another"
          onClick={async () => {
            await completeStage();
            navigate(stageSource);
          }}
          title="Finish this screen and go back to the app to capture a different one"
        >
          Capture another screen
        </button>
      ) : null}
      {onFeedbackSurface ? null : (
        <button
          type="button"
          className="btn ghost sm"
          data-testid="feedback-session-bar-workspace"
          onClick={() => navigate('/feedback')}
        >
          My Feedback
        </button>
      )}
      <button
        type="button"
        className="btn ghost sm feedback-session-bar-end"
        data-testid="feedback-session-bar-end"
        title={
          screenCount === 0 && marks === 0
            ? 'Exit feedback mode — nothing has been captured'
            : undefined
        }
        onClick={(event) => {
          // UAT (Hugh, 2026-07-29): an empty session has nothing to save or
          // discard, so an accidental entry exits in this single click. The
          // confirm dialog remains for sessions holding real work.
          if (screenCount === 0 && marks === 0) {
            void endFeedbackSession({ disposition: 'exit-empty' });
          } else {
            openEndSession(event);
          }
        }}
      >
        End
      </button>
    </div>
  );
}
